import { useState, useEffect } from "react";
import { BarChart3, MessageSquareText } from "lucide-react";
import type { Prompt, PromptType } from "../../../types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { usePromptsStore } from "@/stores/prompts";
import { cn } from "@/lib/utils";

interface EditPromptModalProps {
  prompt: Prompt | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditPromptModal({ prompt, open, onOpenChange }: EditPromptModalProps) {
  const update = usePromptsStore((s) => s.update);

  const [type, setType] = useState<PromptType>("scoring");
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [template, setTemplate] = useState("");
  const [dimensions, setDimensions] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (prompt) {
      setType(prompt.type || "scoring");
      setName(prompt.name);
      setTopic(prompt.category || "general");
      setTemplate(prompt.template);
      setDimensions(prompt.type === "sentiment" ? "" : prompt.dimensions.join(", "));
      setError("");
    }
  }, [prompt]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt) return;
    setError("");

    if (!name.trim() || !template.trim()) {
      setError("Name and template are required");
      return;
    }

    let dims: string[];
    if (type === "sentiment") {
      dims = ["recommendation", "sentiment", "visibility"];
    } else {
      if (!dimensions.trim()) {
        setError("Dimensions are required for scoring prompts");
        return;
      }
      dims = dimensions.split(",").map((d) => d.trim()).filter(Boolean);
      if (dims.length === 0) {
        setError("At least one dimension is required");
        return;
      }
    }

    setSaving(true);
    try {
      await update(prompt.id, {
        name: name.trim(),
        template: template.trim(),
        dimensions: dims,
        type,
        category: topic.trim(),
      });
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!prompt) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Prompt</DialogTitle>
          <DialogDescription>
            Update the prompt configuration
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Type Selector */}
          <div>
            <Label>Type</Label>
            <div className="grid grid-cols-2 gap-2 mt-0.5">
              <button
                type="button"
                onClick={() => setType("scoring")}
                className={cn(
                  "flex items-center gap-3 rounded-md border p-3 text-left transition-all duration-150 cursor-pointer",
                  type === "scoring"
                    ? "border-brand-600 bg-brand-50 ring-1 ring-brand-600/20"
                    : "border-slate-200 bg-white hover:border-slate-300"
                )}
              >
                <BarChart3 className={cn("w-4 h-4 shrink-0", type === "scoring" ? "text-brand-600" : "text-slate-400")} />
                <div>
                  <p className={cn("text-[13px] font-semibold", type === "scoring" ? "text-brand-950" : "text-slate-600")}>
                    Scoring
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Structured dimension scores</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setType("sentiment")}
                className={cn(
                  "flex items-center gap-3 rounded-md border p-3 text-left transition-all duration-150 cursor-pointer",
                  type === "sentiment"
                    ? "border-brand-600 bg-brand-50 ring-1 ring-brand-600/20"
                    : "border-slate-200 bg-white hover:border-slate-300"
                )}
              >
                <MessageSquareText className={cn("w-4 h-4 shrink-0", type === "sentiment" ? "text-brand-600" : "text-slate-400")} />
                <div>
                  <p className={cn("text-[13px] font-semibold", type === "sentiment" ? "text-brand-950" : "text-slate-600")}>
                    Sentiment
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Natural language query</p>
                </div>
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="edit-name">Name</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="edit-topic">Topic</Label>
            <Input
              id="edit-topic"
              placeholder="e.g. Trust signals, local intent, pricing"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />
            <p className="text-[11px] text-slate-400 mt-1.5">
              Topic controls how this prompt is grouped in selectors.
            </p>
          </div>

          <div>
            <Label htmlFor="edit-template">
              {type === "sentiment" ? "Query" : "Template"}
            </Label>
            <Textarea
              id="edit-template"
              rows={6}
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
            />
            {type === "sentiment" && (
              <p className="text-[11px] text-slate-400 mt-1.5">
                Use {"{brands}"} where you want the brand list inserted.
              </p>
            )}
          </div>

          {type === "scoring" && (
            <div>
              <Label htmlFor="edit-dims">Dimensions</Label>
              <Input
                id="edit-dims"
                placeholder="Comma-separated, e.g. reputation, expertise"
                value={dimensions}
                onChange={(e) => setDimensions(e.target.value)}
              />
            </div>
          )}

          {type === "sentiment" && (
            <div className="rounded-md bg-slate-50 border border-slate-100 p-3">
              <p className="text-[11px] font-medium text-slate-500 mb-1">Auto-generated dimensions</p>
              <div className="flex gap-1.5">
                {["recommendation", "sentiment", "visibility"].map(d => (
                  <span key={d} className="inline-flex items-center rounded-md bg-white border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                    {d}
                  </span>
                ))}
              </div>
            </div>
          )}

          {error && <p className="text-[13px] text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
